import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, ArrowRight, AlertCircle, CheckCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { validationRules } from '../../utils/validation';
import { handleFormErrors } from '../../utils/errorHandler';
import SEO from '../../components/common/SEO';

const ForgotPassword = () => {
  const { forgotPassword } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [sentToEmail, setSentToEmail] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError
  } = useForm();

  const onSubmit = async (data) => {
    setIsLoading(true);
    setSentToEmail(data.email);
    try {
      const result = await forgotPassword(data.email);
      
      if (result.success) {
        setEmailSent(true);
      } else if (result.errors) {
        handleFormErrors(result.errors, setError);
        toast.error(result.error || 'Failed to send reset email');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      // Don't reveal if email exists or not for security
      toast.success('If your email exists in our system, you will receive reset instructions shortly.');
      setEmailSent(true);
    } finally {
      setIsLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut'
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <SEO 
        title="Forgot Password"
        description="Reset your Prime Edge account password to regain access to your engineering courses and training materials."
        keywords="forgot password, password reset, account recovery, prime edge"
        canonicalUrl="/forgot-password"
      />
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-md w-full space-y-8"
      >
        {/* Header */}
        <motion.div variants={itemVariants} className="text-center">
          <Link to="/" className="inline-flex items-center space-x-2 mb-6">
            <div className="w-12 h-12 bg-gradient-primary rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-xl">P</span>
            </div>
            <span className="text-2xl font-bold gradient-text">Prime Edge</span>
          </Link>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
            {emailSent ? 'Check Your Email' : 'Forgot Password'}
          </h2>
          <p className="mt-2 text-gray-600 dark:text-gray-300">
            {emailSent 
              ? 'We\'ve sent you instructions to reset your password' 
              : 'Enter your email and we\'ll send you instructions to reset your password'}
          </p>
        </motion.div>

        {/* Form or Success Message */}
        <motion.div variants={itemVariants}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
            {!emailSent ? (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Email Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      {...register('email', validationRules.email)}
                      type="email"
                      className="input pl-12 w-full"
                      placeholder="Enter your email"
                    />
                  </div>
                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.email.message}
                    </p>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn btn-primary btn-lg w-full inline-flex items-center justify-center"
                >
                  {isLoading ? (
                    <>
                      <div className="loading-spinner w-5 h-5 mr-2"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Reset Instructions
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </button>
              </form>
            ) : (
              <div className="flex flex-col items-center justify-center py-6">
                <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Email Sent!</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-2 text-center">
                  We've sent password reset instructions to:
                </p>
                <p className="text-primary-600 font-medium mb-6 text-center">{sentToEmail}</p>
                <p className="text-gray-700 dark:text-gray-300 mb-6 text-center text-sm">
                  Please check your inbox and follow the instructions to reset your password. If you don't see the email, check your spam folder.
                </p>
                <Link to="/login" className="btn btn-outline btn-md">
                  Back to Sign In
                </Link>
              </div>
            )}
          </div>
        </motion.div>

        {/* Sign In Link */}
        {!emailSent && (
          <motion.div variants={itemVariants} className="text-center">
            <p className="text-gray-600 dark:text-gray-300">
              Remember your password?{' '}
              <Link
                to="/login"
                className="font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400"
              >
                Sign in
              </Link>
            </p>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default ForgotPassword;